import { memo } from 'react'
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'
import CheckIcon from '@material-ui/icons/CheckCircleOutline'
import FilledCheckIcon from '@material-ui/icons/CheckCircleRounded'
import Close from '@material-ui/icons/Close'

export enum NoticeType {
  SUCCESS_FILLED = 'successFilled',
  SUCCESS = 'success',
  WARNING = 'warning',
  INFO = 'info',
  DEFAULT = 'default'
}

interface NoticeStyleProps {
  type: NoticeType
  gutterBottom?: boolean
  withColorFromPalette?: boolean
  withSmallerBorderRadius?: boolean
}
interface NoticeProps {
  onClose?: () => void
  onClick?: () => void
  className: string
}

const getTextColor = (
  theme: Theme,
  type: NoticeType,
  withColorFromPalette?: boolean
) => {
  if (withColorFromPalette) {
    if (type === NoticeType.WARNING) {
      return theme.palette.warning.dark
    }
    if (type === NoticeType.SUCCESS) {
      return theme.palette.success.dark
    }
    return theme.palette.info.dark
  }

  if (type === NoticeType.WARNING) {
    return 'rgb(102, 60, 0)'
  }
  return theme.palette.primary.main
}

const getBorderLeft = (
  theme: Theme,
  type: string,
  withColorFromPalette?: boolean
): string => {
  switch (type) {
    case NoticeType.SUCCESS_FILLED:
      return `8px solid ${theme.palette.primary.main}`
    case NoticeType.INFO:
      return `8px solid ${getTextColor(theme, type, withColorFromPalette)}`
    default:
      return 'none'
  }
}

const useStyles = makeStyles<Theme, NoticeStyleProps>((theme) =>
  createStyles({
    container: {
      backgroundColor: ({ type, withColorFromPalette }: NoticeStyleProps) => {
        if (withColorFromPalette) {
          if (type === NoticeType.WARNING) {
            return theme.palette.warning.light
          }
          if (
            type === NoticeType.SUCCESS ||
            type === NoticeType.SUCCESS_FILLED
          ) {
            return theme.palette.success.light
          }
          return theme.palette.info.light
        }

        if (type === NoticeType.WARNING) {
          return 'rgb(255, 244, 229)'
        }
        return 'rgb(22, 82, 151, 0.1)'
      },
      borderLeft: ({ type, withColorFromPalette }: NoticeStyleProps) =>
        getBorderLeft(theme, type, withColorFromPalette),
      borderRadius: ({ withSmallerBorderRadius }: NoticeStyleProps) =>
        withSmallerBorderRadius ? 4 : 6,
      padding: theme.spacing(2),
      position: 'relative',
      display: 'flex',
      userSelect: 'none',
      fontSize: theme.typography.body1.fontSize,
      WebkitTapHighlightColor: 'transparent',
      color: ({ type, withColorFromPalette }: NoticeStyleProps) =>
        getTextColor(theme, type, withColorFromPalette),
      marginBottom: ({ gutterBottom }: NoticeStyleProps) =>
        gutterBottom ? `${theme.spacing(6)}px !important` : 'initial'
    },
    textContainer: {
      margin: 0,
      padding: 0,
      '& > a': {
        color: 'inherit'
      },
      '& > *': {
        '&:first-child': {
          marginTop: 0
        },
        '&:last-child': {
          marginBottom: 0
        }
      }
    },
    close: {
      cursor: 'pointer'
    }
  })
)

const SizedInfoIcon = withStyles((theme) =>
  createStyles({
    root: {
      minWidth: '1.5rem',
      marginRight: theme.spacing(1),
      color: ({ type, withColorFromPalette }: NoticeStyleProps) =>
        getTextColor(theme, type, withColorFromPalette)
    }
  })
)(InfoIcon)

const SizedCheckIcon = withStyles((theme) =>
  createStyles({
    root: {
      minWidth: '1.5rem',
      marginRight: theme.spacing(1),
      color: theme.palette.success.dark
    }
  })
)(CheckIcon)

const SizedFilledCheckIcon = withStyles((theme) =>
  createStyles({
    root: {
      minWidth: '1.5rem',
      marginRight: theme.spacing(1),
      color: theme.palette.success.main
    }
  })
)(FilledCheckIcon)

const Notice = ({
  children,
  onClose,
  onClick,
  type = NoticeType.DEFAULT,
  gutterBottom = false,
  withColorFromPalette,
  withSmallerBorderRadius
}: React.PropsWithChildren<Partial<NoticeProps & NoticeStyleProps>>) => {
  const { container, textContainer, close } = useStyles({
    type,
    gutterBottom,
    withColorFromPalette,
    withSmallerBorderRadius
  })

  return (
    <div
      className={container}
      data-testid="notice"
      onClick={onClick}
      onKeyDown={onClick}
      role="presentation"
    >
      {type === NoticeType.SUCCESS_FILLED && <SizedFilledCheckIcon />}
      {type === NoticeType.SUCCESS && <SizedCheckIcon />}
      {type !== NoticeType.SUCCESS && type !== NoticeType.SUCCESS_FILLED && (
        <SizedInfoIcon
          withColorFromPalette={withColorFromPalette}
          type={type}
        />
      )}
      <div className={textContainer}>{children}</div>
      {onClose && (
        <Close
          color="disabled"
          fontSize="small"
          aria-label="close"
          onClick={onClose}
          className={close}
        />
      )}
    </div>
  )
}

export default memo(Notice)
